import React, { useEffect, useState } from "react";
import {
  Calendar,
  ChevronDown,
  Eraser,
  Plus,
  RotateCw,
  Search,
  SlidersHorizontal,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useDebounce } from "use-debounce";
import { hasPermission } from "../../../utils/permissionUtils";
import { getAllBranches } from "../../../api/hrm/branches/info";
import {
  INVOICE_TYPE_OPTIONS,
  PURCHASE_STATUS_OPTIONS,
  SALE_STATUS_OPTIONS,
} from "../../../api/finance/invoices/invoiceRequests";
import MobileAdvancedFilterSheet from "../../../components/ui/MobileAdvancedFilterSheet";
import {
  applyPeriodChange,
  currentPeriodDefaults,
  yearOptions,
} from "../../reports/finance/financePeriodUtils";

const PERIOD_TYPES = [
  { value: "year", label: "Năm" },
  { value: "quarter", label: "Quý" },
  { value: "month", label: "Tháng" },
];

const STATUS_FILTER_OPTIONS = [
  ...PURCHASE_STATUS_OPTIONS,
  ...SALE_STATUS_OPTIONS.filter(
    (s) => !PURCHASE_STATUS_OPTIONS.some((p) => p.value === s.value)
  ),
];

const buildInitialFilters = () => {
  const period = currentPeriodDefaults();
  return {
    keyword: "",
    invoice_type: "",
    processing_status: "",
    branch_id: "",
    period_type: period.period_type,
    year: period.year,
    quarter: period.quarter,
    month: period.month,
    from_date: period.from_date,
    to_date: period.to_date,
  };
};

const InvoiceFilter = ({ onFilterChange, onNewClick, onRefresh }) => {
  const [advanced, setAdvanced] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [branchOptions, setBranchOptions] = useState([]);
  const [filters, setFilters] = useState(buildInitialFilters);
  const [debouncedKeyword] = useDebounce(filters.keyword, 400);

  const isFiltering =
    filters.keyword !== "" ||
    filters.invoice_type !== "" ||
    filters.processing_status !== "" ||
    filters.branch_id !== "";

  const periodType = filters.period_type || "month";

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    getAllBranches()
      .then((res) => {
        const items = Array.isArray(res) ? res : res?.items || res?.data || [];
        setBranchOptions(
          items.map((b) => ({ value: b.id, label: b.name || "—" }))
        );
      })
      .catch(() => setBranchOptions([]));
  }, []);

  useEffect(() => {
    onFilterChange?.({ ...filters, keyword: debouncedKeyword });
  }, [debouncedKeyword]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateFilter = (name, value) => {
    const updated = { ...filters, [name]: value };
    setFilters(updated);
    if (name !== "keyword") onFilterChange?.(updated);
  };

  const setPeriod = (patch) => {
    const next = applyPeriodChange(filters, patch);
    setFilters(next);
    onFilterChange?.(next);
  };

  const clearFilters = () => {
    const cleared = buildInitialFilters();
    setFilters(cleared);
    onFilterChange?.(cleared);
  };

  const ActionButton = ({ icon: Icon, onClick, title, active }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`h-11 w-11 shrink-0 flex items-center justify-center rounded-xl border-2 transition-all duration-200 ${
        active
          ? "bg-red-50 border-red-300 text-red-600 shadow-sm"
          : "bg-white border-gray-200 text-gray-500 hover:border-red-400 hover:text-red-500"
      }`}
    >
      <Icon size={18} />
    </button>
  );

  const SelectBox = ({ label, value, options, onChange, placeholder }) => (
    <div className="flex flex-col gap-1.5 w-full">
      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
        {label}
      </label>
      <div className="relative group">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pr-10 rounded-2xl text-sm
                     focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all cursor-pointer font-medium shadow-sm"
        >
          <option value="">{placeholder || `Tất cả ${label}`}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={16}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none group-hover:text-red-500 transition-colors"
        />
      </div>
    </div>
  );

  const DateBox = ({ label, value, onChange }) => (
    <div className="flex flex-col gap-1.5 w-full">
      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
        {label}
      </label>
      <div className="relative group">
        <Calendar
          size={16}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-white border border-gray-200 py-3 pl-11 pr-4 rounded-2xl text-sm font-medium outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5"
        />
      </div>
    </div>
  );

  const PeriodTypeSelect = ({ compact = false, fill = false }) => (
    <div
      className={`relative ${
        fill ? "w-full" : compact ? "min-w-[7.5rem]" : "min-w-[8.5rem]"
      }`}
    >
      <select
        value={periodType}
        onChange={(e) => setPeriod({ period_type: e.target.value })}
        aria-label="Loại thời gian"
        className="appearance-none h-11 w-full bg-white border-2 border-gray-200 text-gray-700 pl-3 pr-9 rounded-xl text-sm font-semibold outline-none transition-all cursor-pointer focus:border-red-500 focus:ring-4 focus:ring-red-500/5"
      >
        {PERIOD_TYPES.map((p) => (
          <option key={p.value} value={p.value}>
            {p.label}
          </option>
        ))}
      </select>
      <ChevronDown
        size={16}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
      />
    </div>
  );

  const PeriodValueSelect = ({ compact = false, fill = false }) => {
    const selectCls =
      "appearance-none h-11 w-full bg-white border-2 border-gray-200 text-gray-700 pl-3 pr-9 rounded-xl text-sm font-semibold outline-none transition-all cursor-pointer focus:border-red-500 focus:ring-4 focus:ring-red-500/5";
    const box = fill
      ? "relative flex-1 min-w-0"
      : `relative ${compact ? "min-w-[6.5rem]" : "min-w-[7.5rem]"}`;

    if (periodType === "year") {
      return (
        <div className={fill ? "relative w-full" : box}>
          <select
            value={filters.year || ""}
            onChange={(e) => setPeriod({ year: Number(e.target.value) })}
            aria-label="Chọn năm"
            className={selectCls}
          >
            {yearOptions().map((y) => (
              <option key={y} value={y}>
                Năm {y}
              </option>
            ))}
          </select>
          <ChevronDown
            size={16}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
        </div>
      );
    }

    if (periodType === "quarter") {
      return (
        <div className={`flex items-center gap-2 ${fill ? "w-full" : ""}`}>
          <div className={box}>
            <select
              value={filters.quarter || 1}
              onChange={(e) => setPeriod({ quarter: Number(e.target.value) })}
              aria-label="Chọn quý"
              className={selectCls}
            >
              {[1, 2, 3, 4].map((q) => (
                <option key={q} value={q}>
                  Quý {q}
                </option>
              ))}
            </select>
            <ChevronDown
              size={16}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
          </div>
          <div className={box}>
            <select
              value={filters.year || ""}
              onChange={(e) => setPeriod({ year: Number(e.target.value) })}
              aria-label="Chọn năm"
              className={selectCls}
            >
              {yearOptions().map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <ChevronDown
              size={16}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
          </div>
        </div>
      );
    }

    return (
      <div className={`flex items-center gap-2 ${fill ? "w-full" : ""}`}>
        <div className={box}>
          <select
            value={filters.month || 1}
            onChange={(e) => setPeriod({ month: Number(e.target.value) })}
            aria-label="Chọn tháng"
            className={selectCls}
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>
                Tháng {m}
              </option>
            ))}
          </select>
          <ChevronDown
            size={16}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
        </div>
        <div className={fill ? "relative w-[5.75rem] shrink-0" : box}>
          <select
            value={filters.year || ""}
            onChange={(e) => setPeriod({ year: Number(e.target.value) })}
            aria-label="Chọn năm"
            className={selectCls}
          >
            {yearOptions().map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <ChevronDown
            size={16}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
        </div>
      </div>
    );
  };

  const AdvancedFields = () => (
    <>
      <SelectBox
        label="Loại HĐ"
        value={filters.invoice_type}
        options={INVOICE_TYPE_OPTIONS}
        onChange={(v) => updateFilter("invoice_type", v)}
      />
      <SelectBox
        label="Trạng thái"
        value={filters.processing_status}
        options={STATUS_FILTER_OPTIONS}
        onChange={(v) => updateFilter("processing_status", v)}
      />
      <SelectBox
        label="Chi nhánh"
        value={filters.branch_id}
        options={branchOptions}
        onChange={(v) => updateFilter("branch_id", v)}
      />
      <DateBox
        label="Từ ngày"
        value={filters.from_date}
        onChange={(v) => updateFilter("from_date", v)}
      />
      <DateBox
        label="Đến ngày"
        value={filters.to_date}
        onChange={(v) => updateFilter("to_date", v)}
      />
    </>
  );

  const NewButton = () =>
    hasPermission("finance.invoices", "C") ? (
      <button
        type="button"
        onClick={onNewClick}
        className="hidden md:inline-flex h-11 px-5 items-center gap-2 rounded-xl bg-gradient-to-r from-red-600 to-red-700 text-white text-sm font-bold shadow-lg shadow-red-200 hover:from-red-700 hover:to-red-800 transition-all"
      >
        <Plus size={18} />
        <span>Ghi nhận HĐ</span>
      </button>
    ) : null;

  if (isMobile) {
    return (
      <div className="mb-4 space-y-2.5">
        <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-sm p-3 space-y-3.5">
          {/* Nhóm 1: thời gian */}
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 px-0.5">
              Thời gian
            </p>
            <div className="flex items-stretch gap-2 w-full">
              <div className="w-[30%] min-w-[5.5rem] shrink-0">
                <PeriodTypeSelect compact fill />
              </div>
              <div className="flex-1 min-w-0">
                <PeriodValueSelect compact fill />
              </div>
            </div>
          </div>

          {/* Nhóm 2: tìm kiếm & thao tác */}
          <div className="pt-3 border-t border-gray-100">
            <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 px-0.5">
              Tìm kiếm
            </p>
            <div className="flex items-center gap-2">
              <div className="relative flex-1 group min-w-0">
                <Search
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
                  size={18}
                />
                <input
                  type="text"
                  placeholder="Số HĐ, đối tượng..."
                  value={filters.keyword}
                  onChange={(e) => updateFilter("keyword", e.target.value)}
                  className="w-full h-11 bg-gray-50 border-2 border-gray-200 pl-10 pr-3 rounded-xl focus:outline-none focus:bg-white focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all text-sm font-medium"
                />
              </div>
              <ActionButton
                icon={SlidersHorizontal}
                onClick={() => setAdvanced(!advanced)}
                active={advanced || isFiltering}
                title="Bộ lọc"
              />
              <ActionButton
                icon={RotateCw}
                onClick={onRefresh}
                title="Làm mới"
              />
            </div>
          </div>
        </div>

        <p className="text-[11px] text-gray-400 font-medium px-0.5">
          Trên điện thoại chỉ xem hóa đơn. Ghi nhận / sửa thực hiện trên máy tính.
        </p>

        <MobileAdvancedFilterSheet
          open={advanced}
          onClose={() => setAdvanced(false)}
          title="Bộ lọc hóa đơn"
          footer={
            <>
              <button
                type="button"
                onClick={clearFilters}
                className="flex-1 h-11 rounded-xl border border-gray-200 text-gray-600 font-bold flex items-center justify-center gap-2"
              >
                <Eraser size={16} /> Xóa lọc
              </button>
              <button
                type="button"
                onClick={() => setAdvanced(false)}
                className="flex-1 h-11 rounded-xl bg-red-600 text-white font-bold"
              >
                Áp dụng
              </button>
            </>
          }
        >
          <div className="space-y-4">
            <AdvancedFields />
          </div>
        </MobileAdvancedFilterSheet>
      </div>
    );
  }

  return (
    <div className="mb-6 bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
      <div className="flex flex-wrap items-center gap-3">
        <PeriodTypeSelect />
        <PeriodValueSelect />

        <div className="relative flex-1 min-w-[220px] group">
          <Search
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
            size={18}
          />
          <input
            type="text"
            placeholder="Tìm số HĐ, đối tượng, MST..."
            value={filters.keyword}
            onChange={(e) => updateFilter("keyword", e.target.value)}
            className="w-full h-11 bg-white border border-gray-200 pl-11 pr-4 rounded-xl focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all text-sm font-medium"
          />
        </div>

        <ActionButton
          icon={SlidersHorizontal}
          onClick={() => setAdvanced(!advanced)}
          active={advanced || isFiltering}
          title="Bộ lọc nâng cao"
        />
        <ActionButton icon={RotateCw} onClick={onRefresh} title="Làm mới" />
        {isFiltering && (
          <button
            type="button"
            onClick={clearFilters}
            className="h-11 px-4 rounded-xl border border-gray-200 text-gray-500 hover:text-red-600 hover:border-red-200 text-sm font-bold flex items-center gap-2"
          >
            <Eraser size={16} /> Xóa lọc
          </button>
        )}
        <NewButton />
      </div>

      <AnimatePresence>
        {advanced && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 pt-4 mt-4 border-t border-gray-100">
              <AdvancedFields />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InvoiceFilter;
